import React, { useState, useMemo, useEffect } from 'react'
import { View, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, Icon } from '@components'
import { useLanguage } from '@context'
import { useGetNearbyVisibility, useUpdateNearbyVisibility, NearbyVisibilityScope } from '@hooks'
import { Scope } from '@utils'

// Stable key for the "visible to everyone" option, whose scope is undefined.
const ALL_OPTION_KEY = '__all__'

interface VisibilityOption {
  scope?: NearbyVisibilityScope
  title: string
  description: string
}

interface VisibilityPanelProps {
  onClose: () => void
  showHeader?: boolean
}

export const VisibilityPanel: React.FC<VisibilityPanelProps> = ({ onClose, showHeader = true }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)

  const { data: visibilityData, isLoading } = useGetNearbyVisibility()
  const { mutate: updateVisibility, isPending } = useUpdateNearbyVisibility()

  const [selected, setSelected] = useState<NearbyVisibilityScope | undefined>(visibilityData?.scope ?? undefined)

  // The visibility query may resolve after this panel mounts; sync the selection
  // so a user who previously saved a scope (e.g. HIDDEN) sees it pre-selected.
  useEffect(() => {
    setSelected(visibilityData?.scope ?? undefined)
  }, [visibilityData?.scope])

  const visibilityOptions: VisibilityOption[] = useMemo(() => [
    {
      scope: undefined,
      title: t('nearby.visibilityOptionAllTitle'),
      description: t('nearby.visibilityOptionAllDescription'),
    },
    {
      scope: Scope.PROFESSIONAL,
      title: t('nearby.visibilityOptionProTitle'),
      description: t('nearby.visibilityOptionProDescription'),
    },
    {
      scope: Scope.PRIVATE,
      title: t('nearby.visibilityOptionPrivateTitle'),
      description: t('nearby.visibilityOptionPrivateDescription'),
    },
    {
      scope: 'HIDDEN',
      title: t('nearby.visibilityOptionHiddenTitle'),
      description: t('nearby.visibilityOptionHiddenDescription'),
    },
  ], [t])

  const handleUpdate = () => {
    updateVisibility({ scope: selected }, { onSuccess: onClose })
  }

  return (
    <View style={styles.container}>
      {showHeader && (
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.backButton} activeOpacity={0.7}>
            <Icon name="arrowLeft" size={20} color={theme.colors.secondary} />
          </TouchableOpacity>
          <Text type="titleBold">{t('nearby.modalTitle')}</Text>
        </View>
      )}

      <View style={styles.content}>
        <Text type="bodyBold" style={styles.subtitle}>
          {t('nearby.modalSubtitle')}
        </Text>
        <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.hint}>
          {t('nearby.modalHint')}
        </Text>

        {isLoading ? (
          <ActivityIndicator size="large" color={theme.colors.primary} style={styles.loader} />
        ) : (
          <View style={styles.options}>
            {visibilityOptions.map((option) => {
              const isActive = selected === option.scope
              return (
                <TouchableOpacity
                  key={option.scope ?? ALL_OPTION_KEY}
                  style={styles.option}
                  onPress={() => setSelected(option.scope)}
                  activeOpacity={0.7}
                >
                  <View style={styles.optionText}>
                    <Text type="bodyBold" color={theme.colors.primary}>
                      {option.title}
                    </Text>
                    <Text type="bodySmall" color={theme.colors.greyBlue}>
                      {option.description}
                    </Text>
                  </View>
                  <View style={[styles.radio, isActive && styles.radioActive]}>
                    {isActive && <Icon name="check" size={14} color={theme.colors.white} />}
                  </View>
                </TouchableOpacity>
              )
            })}
          </View>
        )}

        <TouchableOpacity
          style={[styles.updateButton, isPending && styles.updateButtonDisabled]}
          onPress={handleUpdate}
          activeOpacity={0.8}
          disabled={isPending}
        >
          {isPending ? (
            <ActivityIndicator size="small" color={theme.colors.white} />
          ) : (
            <Text type="bodyBold" color={theme.colors.white}>
              {t('nearby.updateButton')}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.grey,
    },
    backButton: {
      padding: 4,
    },
    content: {
      flex: 1,
      padding: 20,
    },
    subtitle: {
      marginBottom: 4,
    },
    hint: {
      marginBottom: 24,
    },
    loader: {
      marginTop: 32,
    },
    options: {
      gap: 24,
      marginBottom: 32,
    },
    option: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    optionText: {
      flex: 1,
      gap: 4,
    },
    radio: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: theme.colors.grey,
      alignItems: 'center',
      justifyContent: 'center',
    },
    radioActive: {
      backgroundColor: theme.colors.green,
    },
    updateButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 24,
      paddingVertical: 14,
      paddingHorizontal: 28,
      alignItems: 'center',
    },
    updateButtonDisabled: {
      opacity: 0.6,
    },
  })
